import * as React from 'react';

import { Badge, Button, ContentCard, ContentSection } from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtomValue, useSetAtom } from 'jotai';
import { LucideMinusCircle, LucidePlusCircle } from 'lucide-react';

import * as accountDetailsState from '../../_state/accountDetails.state';

import { SelectCounterpartyGroup } from './SelectCounterpartyGroup';

import {
  counterpartyGroupAddAccountsAtom,
  counterpartyGroupRemoveAccountsAtom,
} from '~/modules/pricing/_graphql/mutationCounterpartyGroupCrud';
import {
  type AccountCounterpartyGroup,
  counterpartyGroupsByAccountQueryAtom,
} from '~/modules/pricing/_graphql/queryCounterpartyGroupsByAccount';
import { counterpartyGroupsListQueryAtom } from '~/modules/pricing/_graphql/queryCounterpartyGroupsList';
import { OPERATION_TYPE_COLORS } from '~/modules/pricing/types/pricing';

const $remoteAccountGroups = counterpartyGroupsByAccountQueryAtom((get) => ({
  variables: {
    accountId: get(accountDetailsState.$accountId) ?? '',
  },
  skip: !get(accountDetailsState.$accountId),
}));

const $remoteAllGroups = counterpartyGroupsListQueryAtom(() => ({
  variables: {
    limit: 200,
  },
}));

export const AccountCounterpartyGroups = () => {
  const toast = useToast();
  const accountId = useAtomValue(accountDetailsState.$accountId);
  const { data: accountGroups, refetch: refetchAccountGroups } =
    useAtomValue($remoteAccountGroups);
  const { data: allGroupsData } = useAtomValue($remoteAllGroups);

  const addAccounts = useSetAtom(counterpartyGroupAddAccountsAtom({}));
  const removeAccounts = useSetAtom(counterpartyGroupRemoveAccountsAtom({}));

  const [isAdding, setIsAdding] = React.useState(false);
  const [removingGroupId, setRemovingGroupId] = React.useState<string | null>(
    null,
  );

  const groups: AccountCounterpartyGroup[] = accountGroups ?? [];

  const existingGroupIds = React.useMemo(
    () => new Set(groups.map((g) => g.id)),
    [groups],
  );

  const availableGroups = React.useMemo(
    () =>
      (allGroupsData?.counterpartyGroups ?? []).filter(
        (g) => !existingGroupIds.has(g.id),
      ),
    [allGroupsData, existingGroupIds],
  );

  const handleAddToGroup = React.useCallback(
    async (groupId: string) => {
      if (!accountId) return;
      try {
        await addAccounts({ groupId, accountIds: [accountId] });
        toast.success('Account added to counterparty group');
        refetchAccountGroups?.();
        setIsAdding(false);
      } catch (err) {
        toast.error(
          `Failed to add account to group: ${err instanceof Error ? err.message : 'Unknown error'}`,
        );
      }
    },
    [accountId, addAccounts, toast, refetchAccountGroups],
  );

  const handleRemoveFromGroup = React.useCallback(
    async (groupId: string) => {
      if (!accountId) return;
      try {
        setRemovingGroupId(groupId);
        await removeAccounts({ groupId, accountIds: [accountId] });
        toast.success('Account removed from counterparty group');
        refetchAccountGroups?.();
      } catch (err) {
        toast.error(
          `Failed to remove account from group: ${err instanceof Error ? err.message : 'Unknown error'}`,
        );
      } finally {
        setRemovingGroupId(null);
      }
    },
    [accountId, removeAccounts, toast, refetchAccountGroups],
  );

  return (
    <ContentCard className="@AccountCounterpartyGroups">
      <ContentSection
        title={
          <div className="flex items-center justify-between">
            <div>Price List Groups</div>
            {availableGroups.length > 0 && (
              <Button
                variant="link"
                className="px-0 text-sm font-medium"
                preventFocus
                icon={<LucidePlusCircle />}
                onPress={() => setIsAdding(true)}
              >
                Add to Group
              </Button>
            )}
          </div>
        }
      >
        {isAdding && (
          <div className="mb-3 flex items-center gap-2">
            <SelectCounterpartyGroup
              groups={availableGroups}
              onSelect={handleAddToGroup}
              onCancel={() => setIsAdding(false)}
            />
          </div>
        )}
        {groups.length === 0 && !isAdding ? (
          <div className="py-2 text-sm text-neutral/50">
            This account is not assigned to any counterparty groups.
          </div>
        ) : (
          <div className="flex flex-col gap-1">
            {groups.map((group) => (
              <div
                key={group.id}
                className="flex items-center justify-between rounded px-2 py-1.5 hover:bg-neutral/5"
              >
                <div className="flex items-center gap-2">
                  <span className="text-sm">{group.name}</span>
                  <Badge
                    color={OPERATION_TYPE_COLORS[group.operationType]}
                    size="small"
                  >
                    {group.operationType}
                  </Badge>
                </div>
                <Button
                  variant="ghost"
                  color="failure"
                  size="small"
                  icon={<LucideMinusCircle size={14} />}
                  onPress={() => handleRemoveFromGroup(group.id)}
                  isDisabled={removingGroupId === group.id}
                >
                  Remove
                </Button>
              </div>
            ))}
          </div>
        )}
      </ContentSection>
    </ContentCard>
  );
};
